import { DestroyRef, Injectable, inject } from '@angular/core';
import { firstValueFrom, take, timeout } from 'rxjs';
import { AuthService } from '../core/auth.service';
import { FIREBASE_APP } from '../core/firebase.tokens';
import {
  DISCOVERY_MODEL_CONTEXT,
  type DiscoveryPageTool,
} from '../core/discovery-model-context.token';
import { DiscoveryAssignmentApiService } from './discovery-assignment-api.service';
import { isJsonObject, parseBoundedJson } from '../decision-card/bounded-json';
import type { JsonValue } from '@protobuf-ts/runtime';
import {
  DecisionRegisterPresentationInput,
  DecisionRegisterReconciliationAction,
  ExportCanonicalDecisionReceiptResponse,
  PlanDecisionRegisterReconciliationResponse,
  type PlanDecisionRegisterReconciliationRequest,
} from '../../lib/agentic-api/grpc/agentic_discoveryassignment';

const CREATOR_ROLE = 'agentic-discoveryassignment-creator';
const SOURCE_FIELDS = [
  'repository',
  'sourceCommitSha',
  'baseCommitSha',
  'decisionRegisterPath',
  'decisionId',
];
// Both commits may be omitted together: the owner then plans the current default-branch head against
// its first parent and returns the exact pair it used.
const REQUIRED_SOURCE_FIELDS = ['repository', 'decisionRegisterPath', 'decisionId'];
const DECLARED_PRESENTATION_REASON =
  'Prepared from the presentation declared next to the decision register for human review.';
const sourceProperties = {
  repository: { type: 'string', maxLength: 140 },
  sourceCommitSha: { type: 'string', pattern: '^[0-9a-f]{40}$' },
  baseCommitSha: { type: 'string', pattern: '^[0-9a-f]{40}$' },
  decisionRegisterPath: { type: 'string', maxLength: 500 },
  decisionId: { type: 'string', pattern: '^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+$' },
};

export interface DiscoverySession {
  uid: string | null;
  email: string | null;
  authorized: boolean;
  creator: boolean;
  projectId: string;
  signInProvider: string | null;
  authTime: number | null;
}

export interface DiscoveryActor {
  uid: string;
  projectId: string;
}
export interface PreparedDiscoveryDecision {
  assignmentId: string;
  path: string;
  status: string;
  responseRevision: string;
  contentRevision: string;
  presentationRevision: string;
  authorityOriginJson: string | null;
  authorityOriginHash: string | null;
}
export type DiscoveryDecisionSource = Omit<
  PlanDecisionRegisterReconciliationRequest,
  'sourceCommitSha' | 'baseCommitSha'
> & { sourceCommitSha?: string; baseCommitSha?: string };
export interface DiscoveryCommandInputs {
  get_discovery_session: Record<string, never>;
  plan_decision: DiscoveryDecisionSource;
  create_decision: DiscoveryDecisionSource & {
    /** Omit to use the presentation declared next to the register at the source commit. */
    presentationJson?: string;
    nonce: string;
  };
  export_receipt: { assignmentId: string };
}
interface DiscoveryCommandOutputs {
  get_discovery_session: DiscoverySession;
  plan_decision: JsonValue;
  create_decision: PreparedDiscoveryDecision;
  export_receipt: JsonValue;
}
interface DiscoveryCommandDefinition extends Omit<DiscoveryPageTool, 'name' | 'execute'> {
  name: keyof DiscoveryCommandInputs;
  execute(
    input: object,
    options?: { signal?: AbortSignal; expectedActor?: DiscoveryActor },
  ): Promise<unknown>;
}

@Injectable({ providedIn: 'root' })
export class DiscoveryPageTools {
  private readonly auth = inject(AuthService);
  private readonly firebase = inject(FIREBASE_APP);
  private readonly api = inject(DiscoveryAssignmentApiService);
  private readonly context = inject(DISCOVERY_MODEL_CONTEXT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly lifetime = new AbortController();
  private readonly registration = new AbortController();
  private started = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.lifetime.abort());
  }

  /** The ordinary creator page and native tools share this exact checked command boundary. */
  async dispatch<Name extends keyof DiscoveryCommandInputs>(
    name: Name,
    input: DiscoveryCommandInputs[Name],
    signal?: AbortSignal,
    expectedActor?: DiscoveryActor,
  ): Promise<DiscoveryCommandOutputs[Name]> {
    const command = this.tools().find((tool) => tool.name === name);
    if (!command) throw new Error('Unsupported discovery command.');
    return (await command.execute(input, {
      signal,
      expectedActor,
    })) as DiscoveryCommandOutputs[Name];
  }

  /** Registration is optional and never delays ordinary application startup. */
  async register(): Promise<void> {
    if (this.started || !this.context) return;
    this.started = true;
    const tools: DiscoveryPageTool[] = this.tools().map((tool) => ({
      ...tool,
      execute: (input, options) => this.dispatch(tool.name, input as never, options?.signal),
    }));
    const registered: string[] = [];
    const remove = () => {
      this.registration.abort();
      for (const name of registered) this.context?.unregisterTool?.(name);
    };
    this.destroyRef.onDestroy(remove);
    try {
      for (const tool of tools) {
        if (this.registration.signal.aborted) return;
        await this.context.registerTool(tool, { signal: this.registration.signal });
        registered.push(tool.name);
        if (this.registration.signal.aborted) {
          remove();
          return;
        }
      }
    } catch {
      remove();
    }
  }

  private tools(): DiscoveryCommandDefinition[] {
    return [
      {
        name: 'get_discovery_session',
        description:
          'Read only the currently signed-in user’s own UID, email, discovery authorization, creator access, Firebase project, sign-in provider and authentication time. Returns no tokens or credentials.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true },
        execute: async (input, options) => {
          this.input(input, []);
          this.checkCancelled(options?.signal);
          const session = await this.session();
          this.checkCancelled(options?.signal);
          return session;
        },
      },
      {
        name: 'plan_decision',
        description:
          'Plan one decision from a Wiki decision register through the owner. Name both exact commits, or omit both to plan the current default-branch head; the result always names the exact pair used. Requires the current authorized Firebase creator; creates no assignment or human decision.',
        inputSchema: {
          type: 'object',
          properties: sourceProperties,
          required: REQUIRED_SOURCE_FIELDS,
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, options) => {
          const signal = this.callSignal(options?.signal);
          const source = this.source(input, SOURCE_FIELDS);
          const actor = await this.requireAccess(true, signal, options?.expectedActor);
          const plan = await this.api.planDecisionRegister(source, actor.uid!, signal);
          await this.requireSameActor(actor, true, signal);
          return PlanDecisionRegisterReconciliationResponse.toJson(plan);
        },
      },
      {
        name: 'create_decision',
        description:
          'Prepare one owner-verified decision card for human review from a source tuple. Supply bounded presentation JSON, or omit it to use the presentation declared next to the register (decision-presentations/<DECISION-ID>.json) at the source commit. Replans through the owner and uses its configured profile. Reuse the same nonce for retries. Never finalizes an answer or performs Git actions; the participant confirms in the UI.',
        inputSchema: {
          type: 'object',
          properties: {
            ...sourceProperties,
            presentationJson: { type: 'string', maxLength: 1_000_000 },
            nonce: { type: 'string', pattern: '^[A-Za-z0-9][A-Za-z0-9_.-]{0,119}$' },
          },
          required: [...REQUIRED_SOURCE_FIELDS, 'nonce'],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, options) => {
          const signal = this.callSignal(options?.signal);
          const source = this.source(input, [...SOURCE_FIELDS, 'presentationJson', 'nonce']);
          const values = input as Record<string, unknown>;
          const nonce = this.text(values['nonce'], 120);
          if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,119}$/.test(nonce))
            throw new Error('A bounded retry nonce is required.');
          const presentation = this.presentation(values['presentationJson']);
          const actor = await this.requireAccess(true, signal, options?.expectedActor);
          const plan = await this.api.planDecisionRegister(source, actor.uid!, signal);
          await this.requireSameActor(actor, true, signal);
          if (
            plan.action !== DecisionRegisterReconciliationAction.CREATE &&
            !(
              plan.action === DecisionRegisterReconciliationAction.NO_OP &&
              plan.reasonCode === 'DECISION_ASSIGNMENT_CURRENT'
            )
          )
            throw new Error(
              'The owner did not admit creation for this decision: ' + plan.reasonCode,
            );
          const mode = plan.compositionMode?.value;
          const profile = plan.compositionProfile?.value;
          const promptVersion = plan.compositionPromptVersion?.value;
          const provider = plan.compositionProvider?.value;
          const model = plan.compositionModel?.value;
          if (
            (mode !== 'manual' && mode !== 'operator_agent') ||
            !profile ||
            !promptVersion ||
            (mode === 'operator_agent' && (!provider || !model))
          )
            throw new Error('The owner has not supplied a complete composition policy.');
          const result = await this.api.createDecisionRegister(
            {
              plan,
              presentation,
              compositionMode: mode,
              compositionProfile: profile,
              compositionPromptVersion: promptVersion,
              compositionSource:
                mode === 'operator_agent' ? { provider: provider!, model: model! } : undefined,
              contentChangeReason: presentation
                ? 'Prepared through the authenticated discovery creator path for human review.'
                : DECLARED_PRESENTATION_REASON,
            },
            nonce,
            actor.uid!,
            signal,
          );
          await this.requireSameActor(actor, true, signal);
          const assignment = result.assignment;
          if (!assignment?.discoveryAssignmentId)
            throw new Error('The owner did not return a decision card.');
          return {
            assignmentId: assignment.discoveryAssignmentId,
            path: '/agentic/decisions/' + encodeURIComponent(assignment.discoveryAssignmentId),
            status: assignment.status,
            responseRevision: assignment.responseRevision.toString(),
            contentRevision: assignment.contentRevision.toString(),
            presentationRevision: assignment.presentationRevision.toString(),
            authorityOriginJson: assignment.authorityOriginJson?.value ?? null,
            authorityOriginHash: assignment.authorityOriginHash?.value ?? null,
          };
        },
      },
      {
        name: 'export_receipt',
        description:
          'Read the exact public canonical receipt for one finalized Production decision. Requires the current authorized Firebase user and owner-verified participation. Does not publish, approve, finalize or modify anything.',
        inputSchema: {
          type: 'object',
          properties: { assignmentId: { type: 'string', pattern: '^DA[0-9a-f]{32}$' } },
          required: ['assignmentId'],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, options) => {
          const signal = this.callSignal(options?.signal);
          const values = this.input(input, ['assignmentId']);
          const id = this.text(values['assignmentId'], 34);
          if (!/^DA[0-9a-f]{32}$/.test(id)) throw new Error('An exact assignment ID is required.');
          const actor = await this.requireAccess(false, signal, options?.expectedActor);
          const receipt = await this.api.exportCanonicalReceipt(id, actor.uid!, signal);
          await this.requireSameActor(actor, false, signal);
          return ExportCanonicalDecisionReceiptResponse.toJson(receipt);
        },
      },
    ];
  }

  private async session(): Promise<DiscoverySession> {
    const projectId = this.firebase.options.projectId ?? '';
    const user = await firstValueFrom(this.auth.authState().pipe(take(1), timeout(5_000)));
    if (!user)
      return {
        uid: null,
        email: null,
        authorized: false,
        creator: false,
        projectId,
        signInProvider: null,
        authTime: null,
      };
    let claims: Record<string, unknown>;
    try {
      claims = (await user.getIdTokenResult(true)).claims;
    } catch {
      throw new Error('Sign in again to verify your current discovery access.');
    }
    if (this.auth.currentAuthenticatedUser()?.uid !== user.uid)
      throw new Error('The signed-in user changed. Retry from the current session.');
    const authorized =
      claims['authorized'] === true &&
      claims['sub'] === user.uid &&
      claims['aud'] === projectId &&
      claims['iss'] === 'https://securetoken.google.com/' + projectId;
    const roles = claims['roles'];
    const firebase = claims['firebase'];
    const provider = isJsonObject(firebase) ? firebase['sign_in_provider'] : null;
    const authTime = claims['auth_time'];
    return {
      uid: user.uid,
      email: user.email,
      authorized,
      creator: authorized && Array.isArray(roles) && roles.includes(CREATOR_ROLE),
      projectId,
      signInProvider: typeof provider === 'string' && provider.length <= 128 ? provider : null,
      authTime:
        typeof authTime === 'number' && Number.isSafeInteger(authTime) && authTime > 0
          ? authTime
          : null,
    };
  }

  private async requireAccess(
    creator: boolean,
    signal?: AbortSignal,
    expectedActor?: DiscoveryActor,
  ): Promise<DiscoverySession> {
    this.checkCancelled(signal);
    const session = await this.session();
    this.checkCancelled(signal);
    if (
      expectedActor &&
      (session.uid !== expectedActor.uid || session.projectId !== expectedActor.projectId)
    )
      throw new Error('The signed-in creator changed. Refresh access before preparing a decision.');
    if (!session.uid || !session.authorized || (creator && !session.creator))
      throw new Error(
        creator
          ? 'An authorized discovery creator must be signed in.'
          : 'An authorized discovery participant must be signed in.',
      );
    return session;
  }

  private async requireSameActor(
    actor: DiscoverySession,
    creator: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    const current = await this.requireAccess(creator, signal);
    if (current.uid !== actor.uid || current.projectId !== actor.projectId)
      throw new Error('The discovery session changed. No further command was issued.');
  }

  private checkCancelled(signal?: AbortSignal): void {
    if (signal?.aborted || this.lifetime.signal.aborted)
      throw new Error('The discovery tool call was cancelled.');
  }

  private callSignal(signal?: AbortSignal): AbortSignal {
    return signal ? AbortSignal.any([signal, this.lifetime.signal]) : this.lifetime.signal;
  }

  private input(value: object, allowed: string[]): Record<string, unknown> {
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      Object.keys(value).some((key) => !allowed.includes(key))
    )
      throw new Error('The discovery tool input has unsupported fields.');
    return value as Record<string, unknown>;
  }

  private text(value: unknown, maximum: number): string {
    if (typeof value !== 'string' || !value || value.length > maximum)
      throw new Error('A bounded text value is required.');
    return value;
  }

  /** A blank presentation means the one declared in Git; the owner reads and validates it. */
  private presentation(value: unknown): DecisionRegisterPresentationInput | undefined {
    if (value === undefined || value === '') return undefined;
    const parsed = parseBoundedJson(this.text(value, 1_000_000), {
      bytes: 1_000_000,
      depth: 32,
      nodes: 32768,
    });
    if (!isJsonObject(parsed)) throw new Error('The presentation must be one typed JSON object.');
    return DecisionRegisterPresentationInput.fromJson(parsed);
  }

  private commit(value: unknown): string {
    return value === undefined || value === '' ? '' : this.text(value, 40);
  }

  private source(input: object, allowed: string[]): PlanDecisionRegisterReconciliationRequest {
    const values = this.input(input, allowed);
    const result = {
      repository: this.text(values['repository'], 140),
      sourceCommitSha: this.commit(values['sourceCommitSha']),
      baseCommitSha: this.commit(values['baseCommitSha']),
      decisionRegisterPath: this.text(values['decisionRegisterPath'], 500),
      decisionId: this.text(values['decisionId'], 64),
    };
    if (!result.sourceCommitSha !== !result.baseCommitSha)
      throw new Error(
        'Name both the source and base commits, or neither to use the current branch.',
      );
    if (
      !/^[A-Za-z0-9][A-Za-z0-9-]{0,38}\/[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/.test(result.repository) ||
      !/^(?:[0-9a-f]{40})?$/.test(result.sourceCommitSha) ||
      !/^(?:[0-9a-f]{40})?$/.test(result.baseCommitSha) ||
      !/^discovery\/problems\/[a-z][a-z0-9_]*\/[a-z][a-z0-9_]*\/decisions\.md$/.test(
        result.decisionRegisterPath,
      ) ||
      !/^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+$/.test(result.decisionId)
    )
      throw new Error('The exact immutable decision source tuple is invalid.');
    return result;
  }
}
